import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Loader2, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Skeleton } from '../components/ui/skeleton';
import { useToast } from '../components/ui/use-toast';
import AthleteAvatar from '../components/guardian/AthleteAvatar';
import AthleteForm from '../components/guardian/AthleteForm';
import GuardianRequests from '../components/guardian/GuardianRequests';
import { useAuth } from '../hooks/useAuth';
import { useBreadcrumb } from '../hooks/useBreadcrumb';
import { guardianshipsService, type HeldWard } from '../services/guardianships';
import type { AvatarColor } from '../lib/avatar-colors';

/**
 * "My athletes": the children whose DivePeaks accounts this person holds, and
 * everything they can do about them — add one, correct one, change the colour
 * it is drawn in, take one off the account.
 *
 * It exists because a managed account has no profile of its own to visit (that
 * is the point of it), so without this page a parent has nowhere to see what
 * exists in their name. The entry picker at a stage can add a child in
 * passing; this is where they are kept.
 *
 * The same form does the adding and the correcting here and there
 * (AthleteForm), so a child added in the middle of entering a competition is a
 * child managed from this page afterwards.
 */
export default function MyAthletesPage() {
  const { t, i18n } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading } = useAuth();

  const [wards, setWards] = useState<HeldWard[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<HeldWard | null>(null);
  const [removing, setRemoving] = useState<HeldWard | null>(null);
  const [saving, setSaving] = useState(false);

  useBreadcrumb(
    () => ({
      onBack: () => navigate('/profile'),
      segments: [
        { label: t('profile.title'), onClick: () => navigate('/profile') },
        { label: t('guardian.athletes.title') },
      ],
    }),
    [t, navigate]
  );

  // No `t` or `toast` in the deps: this is also what GuardianRequests calls
  // when a request is answered, and a `load` that changed identity on every
  // render would have the effect below re-fetching for ever.
  const load = useCallback(async () => {
    try {
      setWards(await guardianshipsService.wards());
      setLoadError(false);
    } catch {
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authLoading && isAuthenticated) load();
  }, [authLoading, isAuthenticated, load]);

  const replace = (saved: HeldWard) =>
    setWards((current) =>
      current.some((ward) => ward.id === saved.id)
        ? current.map((ward) => (ward.id === saved.id ? saved : ward))
        : [...current, saved]
    );

  const changeColor = async (ward: HeldWard, color: AvatarColor) => {
    try {
      replace(await guardianshipsService.updateWard(ward.id, { avatar_color: color }));
    } catch {
      toast({ title: t('guardian.picker.colorError'), variant: 'destructive' });
    }
  };

  const remove = async () => {
    if (!removing || saving) return;
    setSaving(true);
    try {
      await guardianshipsService.removeWard(removing.id);
      setWards((current) => current.filter((ward) => ward.id !== removing.id));
      toast({ title: t('guardian.athletes.removed', { name: removing.full_name }) });
      setRemoving(null);
    } catch (error) {
      toast({
        title: t('guardian.athletes.removeError'),
        description: error instanceof Error ? error.message : undefined,
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const bornOn = (iso: string) => {
    const date = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(date.getTime())) return iso;
    return date.toLocaleDateString(i18n.language, { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="mx-auto my-6 max-w-2xl space-y-4 px-4">
      <Card>
        <CardHeader>
          <CardTitle>{t('guardian.athletes.title')}</CardTitle>
          <CardDescription>{t('guardian.athletes.description')}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* A child somebody else entered, still waiting on this account:
              answering is the same act as adding one here. */}
          <GuardianRequests onAnswered={load} />

          {loading ? (
            <div className="space-y-2">
              <Skeleton className="h-16 w-full" />
              <Skeleton className="h-16 w-full" />
            </div>
          ) : loadError ? (
            <p className="text-sm text-destructive">{t('guardian.athletes.loadError')}</p>
          ) : wards.length === 0 ? (
            <p className="text-sm text-muted-foreground" data-testid="no-athletes">
              {t('guardian.athletes.empty')}
            </p>
          ) : (
            <ul className="space-y-2">
              {wards.map((ward) => (
                <li
                  key={ward.id}
                  className="flex items-start gap-3 rounded-lg border p-3"
                  data-testid="held-ward"
                >
                  <AthleteAvatar
                    name={ward.name}
                    surname={ward.surname}
                    color={ward.avatar_color}
                    onColorChange={(color) => changeColor(ward, color)}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-medium">{ward.full_name}</p>
                    <p className="text-xs text-muted-foreground" data-testid="held-ward-details">
                      {[
                        t('guardian.accept.bornOn', { date: bornOn(ward.birth_date) }),
                        // The class they compete in, as the entry dialog will
                        // read it — or nothing, and the dialog asks.
                        ward.gender === 'male' || ward.gender === 'female'
                          ? t(`stageEntry.dialog.${ward.gender}`)
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </p>
                    {ward.entries.length > 0 && (
                      <ul className="mt-1 space-y-0.5 text-sm">
                        {ward.entries.map((entry) => (
                          <li key={entry.id}>
                            <Link
                              to={`/competitions/${entry.competition_slug}`}
                              className="text-primary underline underline-offset-2"
                            >
                              {entry.competition_name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  <div className="flex shrink-0 gap-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t('common.edit')}
                      onClick={() => setEditing(ward)}
                      data-testid="edit-ward"
                    >
                      <Pencil className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      aria-label={t('common.delete')}
                      onClick={() => setRemoving(ward)}
                      data-testid="remove-ward"
                    >
                      <Trash2 className="size-4 text-destructive" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}

          <Button type="button" className="gap-1.5" onClick={() => setAdding(true)} data-testid="add-athlete">
            <Plus className="size-4" aria-hidden />
            {t('guardian.athletes.add')}
          </Button>
        </CardContent>
      </Card>

      <Dialog open={adding} onOpenChange={(open) => !open && setAdding(false)}>
        <DialogContent className="max-h-[90dvh] w-[calc(100%-2rem)] grid-cols-[minmax(0,1fr)] overflow-y-auto p-4 sm:max-w-md sm:p-6">
          <DialogHeader>
            <DialogTitle>{t('guardian.athletes.add')}</DialogTitle>
            <DialogDescription>{t('guardian.picker.addDescription')}</DialogDescription>
          </DialogHeader>
          <AthleteForm
            idPrefix="new-athlete"
            onSaved={(ward) => {
              replace(ward);
              setAdding(false);
              toast({ title: t('guardian.athletes.added', { name: ward.full_name }) });
            }}
            onCancel={() => setAdding(false)}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(open) => !open && setEditing(null)}>
        <DialogContent className="max-h-[90dvh] w-[calc(100%-2rem)] grid-cols-[minmax(0,1fr)] overflow-y-auto p-4 sm:max-w-md sm:p-6">
          <DialogHeader>
            <DialogTitle>{t('guardian.athletes.edit', { name: editing?.full_name ?? '' })}</DialogTitle>
            <DialogDescription>{t('guardian.athletes.editHint')}</DialogDescription>
          </DialogHeader>
          {editing && (
            <AthleteForm
              ward={editing}
              idPrefix={`ward-${editing.id}`}
              onSaved={(ward) => {
                replace(ward);
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!removing} onOpenChange={(open) => !open && !saving && setRemoving(null)}>
        <AlertDialogContent className="w-[calc(100%-2rem)] grid-cols-[minmax(0,1fr)]">
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('guardian.athletes.removeTitle', { name: removing?.full_name ?? '' })}
            </AlertDialogTitle>
            {/* What goes and what stays: the account is ours, the entries are
                the organiser's record of a start list. */}
            <AlertDialogDescription>
              {t('guardian.athletes.removeDescription', { name: removing?.full_name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={saving}>{t('common.cancel')}</AlertDialogCancel>
            <AlertDialogAction
              disabled={saving}
              onClick={(event) => {
                event.preventDefault();
                remove();
              }}
              data-testid="confirm-remove-ward"
            >
              {saving && <Loader2 className="mr-2 size-4 animate-spin" />}
              {t('common.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
